import { authService } from '../../services/auth';
import {
  adminApi,
  type AdminBillingDocumentDto,
  type AdminBillingDocumentsPageDto,
} from '../../services/admin.api';
import { onLocaleChange, t } from '../../services/i18n';
import { esc, formatCurrencyFromCents, formatDate } from './admin-shared';

type DocumentTabType = 'invoice' | 'credit_note';

export class RcAdminBillingDocuments extends HTMLElement {
  private items: AdminBillingDocumentDto[] = [];
  private page = 1;
  private totalCount = 0;
  private totalPages = 0;
  private isLoading = false;
  private error = '';
  private disposeLocaleChange?: () => void;

  connectedCallback(): void {
    this.render();
    this.disposeLocaleChange = onLocaleChange(() => this.render());
    this.addEventListener('click', this.handleClick as EventListener);
    if (authService.isAuthenticated) {
      void this.loadDocuments();
    }
  }

  disconnectedCallback(): void {
    this.disposeLocaleChange?.();
    this.removeEventListener('click', this.handleClick as EventListener);
  }

  private get documentType(): DocumentTabType {
    return this.getAttribute('document-type') === 'credit_note'
      ? 'credit_note'
      : 'invoice';
  }

  private async loadDocuments(): Promise<void> {
    this.isLoading = true;
    this.error = '';
    this.render();

    try {
      const result: AdminBillingDocumentsPageDto = this.documentType === 'credit_note'
        ? await adminApi.getCreditNotes({ page: this.page, pageSize: 50 })
        : await adminApi.getInvoices({ page: this.page, pageSize: 50 });

      this.items = result.items;
      this.page = result.page;
      this.totalCount = result.totalCount;
      this.totalPages = result.totalPages;
    } catch (error: any) {
      this.error = error?.message || t(this.documentType === 'credit_note'
        ? 'admin.creditNotes.loadError'
        : 'admin.invoices.loadError');
    } finally {
      this.isLoading = false;
      this.render();
    }
  }

  private render(): void {
    if (!authService.isAuthenticated || !authService.isAdmin) {
      this.innerHTML = `<div class="empty-state">${t('admin.accessDenied')}</div>`;
      return;
    }

    if (this.isLoading) {
      this.innerHTML = `<div class="empty-state">${t('common.loading')}</div>`;
      return;
    }

    if (this.error) {
      this.innerHTML = `<div class="empty-state">${esc(this.error)}</div>`;
      return;
    }

    const titleKey = this.documentType === 'credit_note' ? 'admin.creditNotes.title' : 'admin.invoices.title';
    const subtitleKey = this.documentType === 'credit_note' ? 'admin.creditNotes.subtitle' : 'admin.invoices.subtitle';
    const emptyKey = this.documentType === 'credit_note' ? 'admin.creditNotes.empty' : 'admin.invoices.empty';

    const rows = this.items.length
      ? this.items.map((item) => {
          const amount = item.amountPaidInCents !== 0 ? item.amountPaidInCents : item.totalInCents;
          const paidAt = item.paidAtUtc ? formatDate(item.paidAtUtc) : formatDate(item.createdAtUtc);

          return `
            <tr>
              <td class="admin-cell-name">${esc(item.number || '-')}</td>
              <td class="admin-cell-name">${esc(item.firstName || '-')}</td>
              <td class="admin-cell-name">${esc(item.lastName || '-')}</td>
              <td class="admin-cell-role">${esc(item.status)}</td>
              <td>${formatCurrencyFromCents(amount)}</td>
              <td class="admin-cell-date">${paidAt}</td>
              <td class="admin-cell-role">${esc(item.paymentProviderKey)}</td>
              <td class="admin-center">
                ${item.canDownload
                  ? `<button type="button" class="btn btn-secondary admin-download-btn" data-action="download" data-id="${item.id}" data-file-name="${esc(`${item.number}.pdf`)}">${t('common.download')}</button>`
                  : `<span class="admin-download-unavailable">-</span>`}
              </td>
            </tr>
          `;
        }).join('')
      : `<tr><td colspan="8" class="empty-state admin-users-empty">${t(emptyKey)}</td></tr>`;

    const summary = this.totalCount === 0
      ? t('admin.pagination.noResults')
      : t('admin.pagination.summary', {
          start: String((this.page - 1) * 50 + 1),
          end: String((this.page - 1) * 50 + this.items.length),
          total: String(this.totalCount),
        });

    this.innerHTML = `
      <section class="admin-billing-documents">
        <div class="admin-revenue-header">
          <div>
            <h2 class="admin-revenue-title">${t(titleKey)}</h2>
            <p class="admin-revenue-subtitle">${t(subtitleKey)}</p>
          </div>
        </div>

        <div class="admin-users-toolbar">
          <div class="admin-users-meta">${t('admin.billingDocuments.count', { count: this.totalCount })}</div>
        </div>

        <div class="table-wrap">
          <table class="admin-table admin-billing-documents-table">
            <thead>
              <tr>
                <th>${t('admin.billingDocuments.col.number')}</th>
                <th>${t('admin.billingDocuments.col.firstName')}</th>
                <th>${t('admin.billingDocuments.col.lastName')}</th>
                <th>${t('common.status')}</th>
                <th>${t('admin.billingDocuments.col.amount')}</th>
                <th>${t('admin.billingDocuments.col.date')}</th>
                <th>${t('admin.col.provider')}</th>
                <th>${t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </div>

        <div class="admin-pagination">
          <div class="admin-pagination-info">${summary}</div>
          <div class="admin-pagination-actions">
            <button type="button" class="btn btn-secondary" data-page="${this.page - 1}" ${this.page <= 1 ? 'disabled' : ''}>
              ${t('admin.pagination.previous')}
            </button>
            <span class="admin-pagination-info">${this.totalPages > 0 ? t('admin.pagination.page', { page: String(this.page), totalPages: String(this.totalPages) }) : ''}</span>
            <button type="button" class="btn btn-secondary" data-page="${this.page + 1}" ${this.page >= this.totalPages ? 'disabled' : ''}>
              ${t('admin.pagination.next')}
            </button>
          </div>
        </div>
      </section>
    `;
  }

  private handleClick = (event: Event): void => {
    const target = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-page],[data-action="download"]');
    if (!target || (target instanceof HTMLButtonElement && target.disabled)) {
      return;
    }

    if (target.dataset.action === 'download' && target.dataset.id) {
      void adminApi.downloadBillingDocument(target.dataset.id, target.dataset.fileName);
      return;
    }

    const requestedPage = Number(target.dataset.page ?? '1');
    if (!Number.isFinite(requestedPage) || requestedPage < 1 || requestedPage === this.page) {
      return;
    }

    this.page = requestedPage;
    void this.loadDocuments();
  };
}

if (!customElements.get('rc-admin-billing-documents')) {
  customElements.define('rc-admin-billing-documents', RcAdminBillingDocuments);
}
